"use client";

import { useMemo } from "react";
import type { ApolloClient } from "@apollo/client";
import { useQuery as useApolloQuery } from "@apollo/client/react";
import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@wtc/ui";
import type { SerializedViewState } from "@/components/shared/UniversalCRMTable/types/shared";
import { normalizeToSerializedViewState } from "@/components/shared/UniversalCRMTable/types/shared";
import type { SavedView } from "@/lib/types/saved-views";
import { toSavedViewVisibility } from "@/lib/types/saved-views";
import {
  GetSavedViewsDocument,
  GetSavedViewFavoritesDocument,
  GetAllSavedViewFavoritesDocument,
  ValidateUserAccessToViewDocument,
  type GetSavedViewsQuery,
  type GetSavedViewFavoritesQuery,
  type GetAllSavedViewFavoritesQuery,
  type ValidateUserAccessToViewQuery,
  type ValidateUserAccessToViewQueryVariables,
} from "@/src/gql/graphql";
import { isFeatureEnabled, isFavoritesEnabled } from "./saved-views-utils";

/**
 * The `saved_views` node exactly as the GetSavedViews operation selects it.
 * Derived from codegen so a renamed/removed column is a `tsc` error.
 */
type SavedViewNode = GetSavedViewsQuery["saved_viewsCollection"]["edges"][number]["node"];

/**
 * `filters` is a jsonb column. Read over pg_graphql it arrives as a JSON *string*
 * (see SUPABASE_GRAPHQL_JSONB_READ_IS_STRING) even though codegen types it as an
 * object, so parse defensively and never trust the declared type.
 */
function parseViewFilters(raw: SavedViewNode["filters"]): SerializedViewState {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return normalizeToSerializedViewState(JSON.parse(raw));
    } catch (e) {
      console.error("Failed to parse filters:", raw, e);
      return {};
    }
  }
  return normalizeToSerializedViewState(raw);
}

export function useSavedViews(resource: string) {
  const enabled = isFeatureEnabled();
  const { isAuthenticated } = useUniversalAuth();

  const result = useApolloQuery<GetSavedViewsQuery>(GetSavedViewsDocument, {
    variables: {
      first: 1000,
      after: null,
      resource,
    },
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated || !enabled,
  });

  const views = useMemo<SavedView[]>(() => {
    if (!result.data) return [];

    const edges = result.data.saved_viewsCollection?.edges || [];

    return edges
      .map((edge) => edge?.node)
      .filter((node): node is SavedViewNode => node != null)
      .map((view) => ({
        id: view.id,
        resource: view.resource,
        name: view.name,
        filters: parseViewFilters(view.filters),
        visibility: toSavedViewVisibility(view.visibility),
        shared_with_user_ids: (view.shared_with_user_ids || []).filter(
          (id): id is string => id != null
        ),
        created_by: view.created_by,
        created_at: view.created_at,
        updated_at: view.updated_at,
      }));
  }, [result.data]);

  return {
    data: views,
    isLoading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

export function useSavedViewFavorites(resource?: string) {
  const enabled = isFavoritesEnabled();
  const { appUser, isAuthenticated } = useUniversalAuth();

  const result = useApolloQuery<GetSavedViewFavoritesQuery | GetAllSavedViewFavoritesQuery>(
    resource ? GetSavedViewFavoritesDocument : GetAllSavedViewFavoritesDocument,
    {
      variables: resource
        ? { first: 1000, after: null, userId: appUser?.id, resource }
        : { first: 1000, after: null, userId: appUser?.id },
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      skip: !isAuthenticated || !enabled || !appUser?.id,
    }
  );

  const favorites = useMemo(() => {
    if (!result.data) return [];

    const edges = result.data.saved_view_favoritesCollection?.edges || [];
    return edges
      .map((edge) => edge?.node?.view_id)
      .filter((id): id is string => id != null);
  }, [result.data]);

  return {
    data: favorites,
    isLoading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to validate if a user can access a saved view.
 * Used for sharing validation to ensure users have permission to access the resource.
 */
export function useValidateUserAccessToView() {
  const { isAuthenticated } = useUniversalAuth();

  return useQuery({
    queryKey: ["validate-user-access"],
    enabled: isAuthenticated,
    queryFn: async () => {
      // Placeholder — actual validation happens per-view via validateUserAccessToView
      return { canAccess: true };
    },
    staleTime: Infinity,
  });
}

/**
 * Validate if a specific user can access a saved view.
 * Returns true if the user is the creator, the view is public, or the view is shared with them.
 */
export async function validateUserAccessToView(
  viewId: string,
  userId: string,
  apolloClient: ApolloClient
): Promise<boolean> {
  try {
    // NOTE: the ValidateUserAccessToView operation only declares $viewId — the
    // access decision below is made client-side from the returned row.
    const result = await apolloClient.query<
      ValidateUserAccessToViewQuery,
      ValidateUserAccessToViewQueryVariables
    >({
      query: ValidateUserAccessToViewDocument,
      variables: { viewId },
    });

    const view = result.data?.saved_viewsCollection?.edges?.[0]?.node;
    if (!view) return false;

    if (view.created_by === userId) return true;
    if (view.visibility === "public") return true;
    if (view.visibility === "shared" && view.shared_with_user_ids?.includes(userId)) return true;

    return false;
  } catch (error) {
    console.error("Error validating user access:", error);
    return false;
  }
}
